import type { CompareColumn, CompareFilter, CompareRow, CoverageTally } from './compare-grid.types';

/** True when a value should be treated as "no value" for coverage counting — mirrors the diff util's emptiness rule. */
function isEmptyValue(value: unknown): boolean {
  if (value == null) return true;
  if (typeof value === 'string') return value.trim().length === 0;
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

/**
 * Row-level flags driving the filter chips. Derived from `DiffStatus` only —
 * `needsReview` means enrichment produced nothing or dropped a value the feed
 * had, which is the closest honest analogue to a low-confidence flag here.
 */
export function rowFlags(row: CompareRow): Record<Exclude<CompareFilter, 'all'>, boolean> {
  const fields = Object.values(row.fields);
  return {
    needsReview: row.generationFailed || fields.some((f) => f.status === 'removed'),
    filledGap: fields.some((f) => f.status === 'enriched'),
    edited: fields.some((f) => f.isEdited),
  };
}

/** Applies a filter chip to the loaded rows. `all` passes everything through untouched. */
export function filterRows(rows: readonly CompareRow[], filter: CompareFilter): CompareRow[] {
  if (filter === 'all') return [...rows];
  return rows.filter((row) => rowFlags(row)[filter]);
}

/** Per-chip counts over the loaded rows, used as live badges on the filter chips. */
export function filterCounts(rows: readonly CompareRow[]): Record<CompareFilter, number> {
  const flags = rows.map(rowFlags);
  return {
    all: rows.length,
    needsReview: flags.filter((f) => f.needsReview).length,
    filledGap: flags.filter((f) => f.filledGap).length,
    edited: flags.filter((f) => f.edited).length,
  };
}

/** Field-coverage tally across every row × column — the denominator behind the coverage meter. */
export function coverageTally(
  rows: readonly CompareRow[],
  columns: readonly CompareColumn[],
): CoverageTally {
  let before = 0;
  let now = 0;
  for (const row of rows) {
    for (const column of columns) {
      const field = row.fields[column.key];
      if (!field) continue;
      if (!isEmptyValue(field.feedValue)) before += 1;
      if (!isEmptyValue(field.enrichedValue)) now += 1;
    }
  }
  return { before, now, gained: Math.max(0, now - before), total: rows.length * columns.length };
}

/** Coverage tally for a single column — feeds the `before → now +N` sub-label in each column header. */
export function columnCoverage(rows: readonly CompareRow[], column: CompareColumn): CoverageTally {
  let before = 0;
  let now = 0;
  for (const row of rows) {
    const field = row.fields[column.key];
    if (!field) continue;
    if (!isEmptyValue(field.feedValue)) before += 1;
    if (!isEmptyValue(field.enrichedValue)) now += 1;
  }
  return { before, now, gained: Math.max(0, now - before), total: rows.length };
}
